import { useGetChartingRecallSettings } from "@src/appV2/Charting/api/useGetChartingRecallSettings";
import { EMPLOYEE_ROLES } from "@src/appV2/Employee/constants";
import { useIsHomeHealthEnabled } from "@src/appV2/ExperimentalHomeHealth/lib/useIsHomeHealthEnabled";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { PlacementAccessControlStatus } from "@src/appV2/FeatureFlags/CbhFeatureFlags";
import { useIsInterviewAvailabilityMenuVisible } from "@src/appV2/InterviewSettings/useIsInterviewAvailabilityMenuVisible";
import { useGetPlacementsAccessLevel } from "@src/appV2/Placements/useGetPlacementsAccessLevel";
import { FACILITY_ROLES, FACILITY_USER_PERMISSIONS } from "@src/constants/facility";
import { evaluateWorkplaceToWorkplaceReferrals } from "@src/containers/workplaceReferAndEarn/utils";
import { useFacilityOnboardingFlag } from "@src/hooks/useFacilityOnboardingFlag";
import { type SessionType } from "@src/modules/interface";
import constate from "constate";
import { useFlags } from "launchdarkly-react-client-sdk";
import includes from "lodash/includes";
import { useSelector } from "react-redux";

import { hasAnyTrueValue } from "./utils";
import { useShowLiveMapView } from "@src/containers/app/hooks/showLiveMapView";
import { useShowContractTerms } from "@src/containers/app/hooks/showContractTerms";
import { isDefined } from "@clipboard-health/util-ts";
import { AnalyticsType } from "@src/appV2/Analytics/constants";

export interface WorkplaceMenuPriviliges {
  homeHealthSection?: {
    cases: boolean;
    visitVerification: boolean;
  };
  workplaceOnboarding?: boolean;
  dailyView?: boolean;
  calendar?: boolean;
  chat?: boolean;
  professionalsSection?: { myProfessionals: boolean; browseProfessionals: boolean };
  permanentHiringSection?: {
    placementsLandingPage: boolean;
    interviewSchedule: boolean;
    applicationAndJobs: boolean;
    browseTalentPool: boolean;
    interviewAvailability: boolean;
  };
  charting?: boolean;
  analyticsSection?: {
    workplaceAnalytics: boolean;
    corporateAnalytics: boolean;
  };
  corporateCenterSection?: {
    corporateAnalytics: boolean;
    spendingControls: boolean;
  };
  liveWorkerETA?: boolean;
  workplaceRulesQuiz?: boolean;
  managementSection?: {
    myAccount: boolean;
    teamMembers: boolean;
    workplaceInformation: boolean;
    settings: boolean;
    timeclockingSettings: boolean;
  };
  billingSection?: {
    upcomingCharges: boolean;
    invoiceHistory: boolean;
    contractTerms: boolean;
    workplaceSpendingControls: boolean;
  };
  helpCenter?: boolean;
  referAndEarn?: boolean;
  // Route authorization flags (not for menu visibility)
  routeAuth?: {
    isWorkplaceOrAdmin: boolean;
    hasPermissionToAccessWorkerDirectory: boolean;
    isDocumentAuthorized: boolean;
    hasDefaultWorkplacePermissions: boolean;
    isFacilityInvoiceDisputeAuthorized: boolean;
    isInvoiceViewAuthorized: boolean;
  };
}

/* eslint-disable complexity */
export function useGetWorkplaceMenuPriviliges(): WorkplaceMenuPriviliges {
  const session = useSelector((state: SessionType) => state.session);
  const { admin: isAdmin, adminProfile, user, profile, type } = session ?? {};
  const { access: roles, permissions } = user ?? {};

  const isWorkplace = type === "FACILITY";

  const workplaceOnboarding = useFacilityOnboardingFlag({
    workplaceCreationDate: profile?.createdAt,
    roles: roles ?? [],
    isAdmin,
    isWorkplace,
  });

  const hasDefaultWorkplacePermissions =
    [
      FACILITY_ROLES.ADMIN,
      FACILITY_ROLES.SHIFT_MANAGEMENT,
      FACILITY_ROLES.DOCUMENTS,
      FACILITY_ROLES.INVOICES,
      FACILITY_ROLES.CORPORATE_ADMIN,
    ].some((role) => includes(roles, role)) || isAdmin;

  const isHomeHealthEnabled = useIsHomeHealthEnabled();
  const isHomeHealthHiddenMenuItemsEnabled = useCbhFlag(
    CbhFeatureFlag.HOME_HEALTH_MENU_ITEMS_HIDDEN,
    {
      defaultValue: true,
    }
  );

  const hideMenuItemsForHomeHealth =
    isHomeHealthEnabled && isHomeHealthHiddenMenuItemsEnabled && !isAdmin;

  // Home Health specific menu items
  const homeHealthCases = isHomeHealthEnabled;
  const homeHealthVisitVerification = isHomeHealthEnabled;
  const homeHealthSection = {
    cases: homeHealthCases,
    visitVerification: homeHealthVisitVerification,
  };

  const isDailyViewEnabled = useCbhFlag(CbhFeatureFlag.DAILY_VIEW_ENABLED, {
    defaultValue: false,
  });

  const dailyView =
    isDailyViewEnabled && hasDefaultWorkplacePermissions && !hideMenuItemsForHomeHealth;

  const calendar = hasDefaultWorkplacePermissions && !hideMenuItemsForHomeHealth;

  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
  const chat =
    (isAdmin && adminProfile?.roles?.includes(EMPLOYEE_ROLES.VIEW_WORKPLACE_CHAT)) ||
    includes(roles, FACILITY_ROLES.ADMIN) ||
    includes(roles, FACILITY_ROLES.DOCUMENTS) ||
    includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT);
  /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

  const myProfessionals = true;

  const hasPermissionToAccessWorkerDirectory =
    includes(roles, FACILITY_ROLES.ADMIN) ||
    includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT) ||
    includes(roles, FACILITY_ROLES.RECRUITMENT) ||
    isAdmin;

  const ldFlags = useFlags();
  const hideBrowserWorkers = ldFlags?.[CbhFeatureFlag.HIDE_BROWSER_WORKERS];

  const browseProfessionals =
    hasPermissionToAccessWorkerDirectory && !isHomeHealthEnabled && !hideBrowserWorkers;

  const professionalsSection = { myProfessionals, browseProfessionals };

  const { isEnabled: isPlacementsEnabled, accessLevel: placementsAccessLevel } =
    useGetPlacementsAccessLevel();

  const isInterviewAvailabilityVisible = useIsInterviewAvailabilityMenuVisible();

  const placementsLandingPage = placementsAccessLevel === PlacementAccessControlStatus.LANDING_PAGE;

  const interviewSchedule = isPlacementsEnabled;

  const applicationAndJobs = isPlacementsEnabled;

  const browseTalentPool =
    isPlacementsEnabled && placementsAccessLevel !== PlacementAccessControlStatus.EDIT_FREE;

  const interviewAvailability = isPlacementsEnabled && isInterviewAvailabilityVisible;

  const hasPermissionToRecruit =
    includes(roles, FACILITY_ROLES.RECRUITMENT) ||
    includes(roles, FACILITY_ROLES.ADMIN) ||
    includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT) ||
    isAdmin;

  const permanentHiringSection = {
    placementsLandingPage,
    interviewSchedule,
    applicationAndJobs,
    browseTalentPool,
    interviewAvailability,
  };

  const { chartingRecallEnabled: charting } = useGetChartingRecallSettings({
    workplaceId: profile?.userId,
    options: { enabled: isWorkplace },
  });

  const analyticsConfig = useCbhFlag(CbhFeatureFlag.FACILITY_EXPLO_ANALYTICS_ENABLED, {
    defaultValue: [],
  });
  const hasAnalyticsPermissions =
    analyticsConfig.length > 0 && (isAdmin || includes(roles, FACILITY_ROLES.ADMIN));
  const workplaceAnalytics = isDefined(
    analyticsConfig.find((e) => e.type === AnalyticsType.WORKPLACE_FLEX)
  );
  const corporateAnalytics = isDefined(
    analyticsConfig.find((e) => e.type === AnalyticsType.CORPORATE_FLEX)
  );
  const analyticsSection = { workplaceAnalytics, corporateAnalytics };

  // Corporate Center section (spending controls enabled)
  const spendingControlsConfig = useCbhFlag(CbhFeatureFlag.CONFIGURE_SPENDING_CONTROLS, {
    defaultValue: { enabled: false },
  });
  const isSpendingControlsEnabled = spendingControlsConfig.enabled;
  const isSpendingControlsInternalOnly =
    spendingControlsConfig.enabled && spendingControlsConfig.internalUseOnly;
  const hasCorporateAdminRole = includes(roles, FACILITY_ROLES.CORPORATE_ADMIN);
  const hasCorporateAnalyticsAccess = corporateAnalytics;
  // When internal-only, only Clipboard employees (isAdmin) can access spending controls
  const canAccessCorporateSpendingControls = isSpendingControlsInternalOnly
    ? isAdmin
    : isAdmin || hasCorporateAdminRole;
  const shouldShowCorporateCenter =
    isSpendingControlsEnabled &&
    (canAccessCorporateSpendingControls || hasCorporateAnalyticsAccess);
  const corporateCenterSection = shouldShowCorporateCenter
    ? {
        corporateAnalytics: hasCorporateAnalyticsAccess,
        spendingControls: canAccessCorporateSpendingControls,
      }
    : undefined;

  const shouldShowLiveMapView = useShowLiveMapView();

  const liveWorkerETA =
    shouldShowLiveMapView && hasDefaultWorkplacePermissions && !isHomeHealthEnabled;

  const workplaceRulesQuiz = ldFlags?.[CbhFeatureFlag.ROLLOUT_QUIZ_V2_FACILITY_RULES_AI] ?? false;

  const myAccount = ldFlags?.[CbhFeatureFlag.MY_ACCOUNT_PAGE] && !isAdmin;

  const teamMembers =
    includes(roles, FACILITY_ROLES.ADMIN) ||
    includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT) ||
    includes(roles, FACILITY_ROLES.INVOICES) ||
    isAdmin;

  const workplaceInformation = !workplaceRulesQuiz && hasDefaultWorkplacePermissions;

  const settings =
    (includes(roles, FACILITY_ROLES.ADMIN) || isAdmin) && !hideMenuItemsForHomeHealth;

  const timeclockingSettings = includes(roles, FACILITY_ROLES.ADMIN) && !settings;

  const managementSection = {
    myAccount,
    teamMembers,
    workplaceInformation,
    settings,
    timeclockingSettings,
  };

  const hasBillingRights =
    isAdmin || includes(roles, FACILITY_ROLES.ADMIN) || includes(roles, FACILITY_ROLES.INVOICES);

  const upcomingCharges =
    hasBillingRights ||
    includes(permissions, FACILITY_USER_PERMISSIONS.ACCESS_UPCOMING_CHARGES) ||
    includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT);

  const invoiceHistory =
    hasBillingRights || includes(permissions, FACILITY_USER_PERMISSIONS.MANAGE_INVOICES);

  const shouldShowContractTerms = useShowContractTerms(false);

  const hasViewContractTermsRights =
    hasBillingRights || includes(permissions, FACILITY_USER_PERMISSIONS.ACCESS_CONTRACT_TERMS);

  const contractTerms =
    shouldShowContractTerms && hasViewContractTermsRights && !hideMenuItemsForHomeHealth;

  // Workplace spending controls: enabled for facility admins and staffing coordinators
  // who are NOT corporate admins (they use the Corporate Center page instead)
  // When internal-only, workplace spending controls are completely hidden
  // Clipboard employees can still access by direct URL, but don't show in menu
  const workplaceSpendingControls =
    isSpendingControlsEnabled &&
    !isSpendingControlsInternalOnly &&
    !isAdmin &&
    !hasCorporateAdminRole &&
    (includes(roles, FACILITY_ROLES.ADMIN) || includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT));

  const billingSection = {
    upcomingCharges,
    invoiceHistory,
    contractTerms,
    workplaceSpendingControls,
  };

  const helpCenter = true;

  const referAndEarn = evaluateWorkplaceToWorkplaceReferrals(ldFlags, user, isWorkplace, isAdmin);

  // Route authorization (mirrors legacy appLayout.tsx checks)
  const isWorkplaceOrAdmin = isWorkplace || isAdmin;

  const isDocumentAuthorized =
    adminProfile?.roles?.includes(EMPLOYEE_ROLES.DOCUMENTS) ||
    (isWorkplace &&
      (includes(roles, FACILITY_ROLES.ADMIN) ||
        includes(roles, FACILITY_ROLES.SHIFT_MANAGEMENT) ||
        includes(roles, FACILITY_ROLES.DOCUMENTS)));

  const isFacilityInvoiceDisputeAuthorized =
    isWorkplace &&
    !isAdmin &&
    (includes(roles, FACILITY_ROLES.ADMIN) ||
      includes(roles, FACILITY_ROLES.INVOICES) ||
      includes(permissions, FACILITY_USER_PERMISSIONS.MANAGE_INVOICES));

  const isInvoiceViewAuthorized =
    isAdmin ||
    includes(roles, FACILITY_ROLES.ADMIN) ||
    includes(roles, FACILITY_ROLES.INVOICES) ||
    includes(permissions, FACILITY_USER_PERMISSIONS.MANAGE_INVOICES);

  const routeAuth = {
    isWorkplaceOrAdmin,
    hasPermissionToAccessWorkerDirectory,
    isDocumentAuthorized: Boolean(isDocumentAuthorized),
    hasDefaultWorkplacePermissions,
    isFacilityInvoiceDisputeAuthorized: Boolean(isFacilityInvoiceDisputeAuthorized),
    isInvoiceViewAuthorized: Boolean(isInvoiceViewAuthorized),
  };

  if (!isWorkplace) {
    return { routeAuth };
  }

  return {
    homeHealthSection: isHomeHealthEnabled ? homeHealthSection : undefined,

    workplaceOnboarding,
    dailyView,
    calendar,
    chat,

    professionalsSection:
      (user
        ? isAdmin ||
          includes(user.access, FACILITY_ROLES.ADMIN) ||
          includes(user.access, FACILITY_ROLES.INVOICES) ||
          includes(user.access, FACILITY_ROLES.SHIFT_MANAGEMENT)
        : false) && hasAnyTrueValue(professionalsSection)
        ? professionalsSection
        : undefined,

    permanentHiringSection:
      hasPermissionToRecruit && hasAnyTrueValue(permanentHiringSection)
        ? permanentHiringSection
        : undefined,

    charting,

    analyticsSection:
      hasAnalyticsPermissions ?? hasAnyTrueValue(analyticsSection) ? analyticsSection : undefined,

    corporateCenterSection,

    liveWorkerETA,

    workplaceRulesQuiz,

    managementSection: hasAnyTrueValue(managementSection) ? managementSection : undefined,

    billingSection: hasAnyTrueValue(billingSection) ? billingSection : undefined,

    helpCenter,
    referAndEarn,
    routeAuth,
  };
}
/* eslint-enable complexity */

export const [WorkplaceMenuPriviligesProvider, useWorkplaceMenuPriviligesProviderContext] =
  constate(useGetWorkplaceMenuPriviliges);
